// =============================================================================
// MEMORY THUMBNAIL STRIP
// =============================================================================
// Scrollable strip of photo thumbnails for quick navigation

import { useRef, useEffect } from 'react';
import type { MemoryPhoto } from '@chlann/shared';
import { withAuthToken } from '../../api/client.js';

interface MemoryThumbnailStripProps {
  photos: MemoryPhoto[];
  currentIndex: number;
  onSelect: (index: number) => void;
}

export function MemoryThumbnailStrip({
  photos,
  currentIndex,
  onSelect,
}: MemoryThumbnailStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Scroll to keep active thumbnail visible
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      const container = containerRef.current;
      const active = activeRef.current;

      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();

      // Check if active thumbnail is outside visible area
      if (activeRect.left < containerRect.left) {
        container.scrollLeft -= containerRect.left - activeRect.left + 20;
      } else if (activeRect.right > containerRect.right) {
        container.scrollLeft += activeRect.right - containerRect.right + 20;
      }
    }
  }, [currentIndex]);

  return (
    <div
      ref={containerRef}
      className="flex gap-2 overflow-x-auto bg-bogwood-900 px-4 py-3 scrollbar-thin scrollbar-track-ink-1 scrollbar-thumb-bogwood-600"
    >
      {photos.map((photo, index) => {
        const isActive = index === currentIndex;

        return (
          <button
            key={photo.id}
            ref={isActive ? activeRef : null}
            onClick={() => { onSelect(index); }}
            className={`relative flex-shrink-0 overflow-hidden rounded-lg transition-all ${
              isActive
                ? 'ring-2 ring-primary ring-offset-2 ring-offset-ink-1'
                : 'opacity-60 hover:opacity-100'
            }`}
            aria-label={`Photo ${index + 1} of ${photos.length}`}
            aria-current={isActive ? 'true' : undefined}
          >
            <img
              src={withAuthToken(photo.thumbnailUrl)}
              alt={photo.caption || `Photo ${index + 1}`}
              className="h-16 w-16 object-cover"
              loading="lazy"
            />

            {/* Index indicator */}
            <span className="absolute bottom-0.5 right-0.5 rounded bg-ink-1/80 px-1.5 py-0.5 text-xs font-medium text-white">
              {index + 1}
            </span>
          </button>
        );
      })}
    </div>
  );
}
